import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import Axios, { type AxiosRequestConfig } from "axios";
import { loginRequest } from "@/security/msal";

export const axios = Axios.create({
  baseURL: "/api"
});

const secureAxios = Axios.create({
  baseURL: "/api"
});

export const getData = async <T>(
  url: string,
  config?: AxiosRequestConfig<T>
) => {
  const response = await secureAxios.get<T>(url, config);
  return response.data;
};

export function useApi() {
  const { instance } = useMsal();
  const getAccessToken = async () => {
    try {
      const accounts = instance.getAllAccounts();
      const account = accounts[0];
      return await instance.acquireTokenSilent({
        ...loginRequest,
        account
      });
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(loginRequest);
      } else {
        throw e;
      }
    }
  };

  secureAxios.interceptors.request.use(async (config) => {
    // The access token expires once per hour, so this will occasionally prepend a token refresh request, which will make API responses feel slow
    // If that becomes a problem, we could put a `setTimeout` in a `useEffect` hook and manually refresh it in the background, but that feels flaky
    const authResult = await getAccessToken();
    if (authResult) {
      const { accessToken } = authResult;
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    config.headers.Accept = "application/json";
    return config;
  });

  return { secureAxios, getAccessToken };
}

export default useApi;
